// OneLogin OIDC wrapper using oidc-client library (like OneLogin"s official Angular sample)
// Based on: https://github.com/onelogin/onelogin-oidc-angular

// Import oidc-client - it should be available since it"s required in vendor.js
const Oidc = require("oidc-client");

// Create a wrapper that mimics the Auth0 angular interface for easier transition
const oneLoginModuleName = "onelogin.oidc";

// Create the Angular module
if (typeof angular !== "undefined") {
    angular.module(oneLoginModuleName, [])
        .provider("oneLoginOidc", function() {
            let config = {};
            let userManager = null;
            
            this.init = function(initConfig) {
                config = initConfig;
                
                // DIAGNOSTIC: Check oidc-client availability
                if (!Oidc) {
                    // eslint-disable-next-line no-console
                    console.error("❌ oidc-client library not found! Using fallback implementation.");
                    return;
                }
                
                // Extract domain without protocol if it exists
                const domain = config.domain.replace(/^https?:\/\//, "");
                
                // Configure OIDC UserManager for OneLogin OIDC v2
                const userManagerSettings = {
                    authority: `https://${domain}/oidc/2`,  // OneLogin OIDC v2 authority (required)
                    client_id: config.clientID,
                    redirect_uri: config.redirectUri,
                    response_type: "id_token token",  // Implicit flow
                    scope: config.scope,
                    filterProtocolClaims: true,
                    loadUserInfo: true,
                    automaticSilentRenew: false,  // Keep disabled to avoid complexity
                    silent_redirect_uri: config.redirectUri  // Use same for now
                    // Using default state management (no custom stores)
                };
                
                // Create UserManager with oidc-client
                try {
                    userManager = new Oidc.UserManager(userManagerSettings);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("❌ Failed to create UserManager:", error);
                    // eslint-disable-next-line no-console
                    console.error("Will use fallback implementation");
                }
            };
            
            this.$get = ["$q", "$window", "$log", function($q, $window, $log) {
                
                // Helper function to add custom claims expected by TrackerBase API
                function enhanceAuthResultWithCustomClaims(authResult, user) {
                    $log.debug("Enhancing auth result with custom claims");
                    $log.debug("AuthResult scope:", authResult.scope);
                    
                    // Parse the ID token to get user info if available
                    let idTokenPayload = null;
                    if (authResult.idToken) {
                        try {
                            // Decode the JWT payload (second part of the token)
                            const base64Payload = authResult.idToken.split(".")[1];
                            const decodedPayload = atob(base64Payload);
                            idTokenPayload = JSON.parse(decodedPayload);
                            $log.debug("ID token payload:", idTokenPayload);
                        } catch (error) {
                            $log.warn("Failed to parse ID token:", error);
                        }
                    }
                    
                    // Get user profile from either the user object or ID token
                    const profile = (user && user.profile) || idTokenPayload || {};
                    
                    // OneLogin returns custom parameters in a nested 'params' object
                    const paramsData = profile.params || {};
                    
                    $log.debug("User profile for claims mapping:", profile);
                    
                    // Create enhanced authResult with custom claims
                    const enhancedResult = Object.assign({}, authResult);
                    
                    // Check what scopes were granted
                    const grantedScopes = authResult.scope ? authResult.scope.split(" ") : [];
                    $log.debug("Granted scopes:", grantedScopes);
                    
                    // Add custom claims that the TrackerBase API expects
                    // Map standard OIDC claims to custom namespaced claims
                    const customClaims = {};
                    
                    // OneLogin puts custom claims in the 'params' object - use those first, then fallback to direct claims
                    const useParamsData = paramsData && Object.keys(paramsData).length > 0;
                    
                    // Email claims - prefer params data, fallback to profile
                    if (useParamsData && paramsData["https://ab21.org/claims/email"]) {
                        customClaims["https://ab21.org/claims/email"] = paramsData["https://ab21.org/claims/email"];
                        customClaims["https://ab21.org/claims/email_verified"] = paramsData["https://ab21.org/claims/email_verified"] !== undefined ? paramsData["https://ab21.org/claims/email_verified"] : false;
                    } else if (profile.email) {
                        customClaims["https://ab21.org/claims/email"] = profile.email;
                        customClaims["https://ab21.org/claims/email_verified"] = profile.email_verified !== undefined ? profile.email_verified : false;
                    }
                    
                    // Groups claims - prefer params data, fallback to profile
                    if (useParamsData && paramsData["https://ab21.org/claims/groups"]) {
                        customClaims["https://ab21.org/claims/groups"] = paramsData["https://ab21.org/claims/groups"];
                    } else if (profile.groups) {
                        customClaims["https://ab21.org/claims/groups"] = profile.groups;
                    } else {
                        // Try alternative group claim names that OneLogin might use
                        customClaims["https://ab21.org/claims/groups"] = profile.memberOf || profile.roles || [];
                    }
                    
                    // Serial number - prefer params data, fallback to profile
                    if (useParamsData && paramsData["https://ab21.org/claims/serialnumber"]) {
                        customClaims["https://ab21.org/claims/serialnumber"] = paramsData["https://ab21.org/claims/serialnumber"];
                    } else if (profile.sub || profile.serialnumber || profile.objectguid || profile.oid) {
                        customClaims["https://ab21.org/claims/serialnumber"] = profile.sub || profile.serialnumber || profile.objectguid || profile.oid;
                    }
                    
                    // Log what we're mapping
                    $log.debug("Custom claims being added:", customClaims);
                    
                    // Add custom claims to the authResult for client-side use
                    enhancedResult.customClaims = customClaims;
                    enhancedResult.profile = Object.assign({}, profile, customClaims);
                    
                    return enhancedResult;
                }
                
                return {
                    authorize: function() {
                        $log.debug("OneLogin authorize called");
                        $log.debug("=== SCOPE DEBUG ===");
                        $log.debug("Requested scope:", config.scope);
                        $log.debug("Scope includes params?", config.scope.includes("params"));
                        $log.debug("=== END SCOPE DEBUG ===");
                        
                        // DIAGNOSTIC: Check if we have oidc-client
                        $log.debug("Oidc library available:", !!Oidc);
                        $log.debug("UserManager available:", !!(Oidc && Oidc.UserManager));
                        $log.debug("userManager initialized:", !!userManager);
                        
                        if (!userManager) {
                            $log.error("UserManager not initialized - falling back to manual redirect");
                            
                            // FALLBACK: Manual redirect with nonce
                            const domain = config.domain.replace(/^https?:\/\//, "");
                            const state = Math.random().toString(36).substr(2, 9);
                            const nonce = Math.random().toString(36).substr(2, 16);
                            
                            const authUrl = `https://${domain}/oidc/2/auth` +
                                `?client_id=${encodeURIComponent(config.clientID)}` +
                                `&redirect_uri=${encodeURIComponent(config.redirectUri)}` +
                                `&response_type=${encodeURIComponent(config.responseType)}` +
                                `&scope=${encodeURIComponent(config.scope)}` +
                                `&state=${encodeURIComponent(state)}` +
                                `&nonce=${encodeURIComponent(nonce)}`;
                            
                            $log.debug("FALLBACK: Manual auth URL with nonce:", authUrl);
                            $log.debug("FALLBACK: Nonce parameter included:", authUrl.includes("nonce="));
                            
                            // Store for validation
                            sessionStorage.setItem("oidc_state", state);
                            sessionStorage.setItem("oidc_nonce", nonce);
                            
                            $window.location.href = authUrl;
                            return;
                        }
                        
                        // Use oidc-client"s signinRedirect (handles all OIDC parameters automatically)
                        $log.debug("Using oidc-client UserManager for authentication");
                        userManager.signinRedirect().then(() => {
                            $log.debug("Redirecting to OneLogin via oidc-client...");
                        }).catch(error => {
                            $log.error("Authentication redirect failed:", error);
                            $log.error("Falling back to manual redirect...");
                            
                            // FALLBACK if oidc-client fails
                            const domain = config.domain.replace(/^https?:\/\//, "");
                            const state = Math.random().toString(36).substr(2, 9);
                            const nonce = Math.random().toString(36).substr(2, 16);
                            
                            const authUrl = `https://${domain}/oidc/2/auth` +
                                `?client_id=${encodeURIComponent(config.clientID)}` +
                                `&redirect_uri=${encodeURIComponent(config.redirectUri)}` +
                                `&response_type=${encodeURIComponent(config.responseType)}` +
                                `&scope=${encodeURIComponent(config.scope)}` +
                                `&state=${encodeURIComponent(state)}` +
                                `&nonce=${encodeURIComponent(nonce)}`;
                            
                            $log.debug("ERROR FALLBACK: Manual auth URL with nonce:", authUrl);
                            
                            sessionStorage.setItem("oidc_state", state);
                            sessionStorage.setItem("oidc_nonce", nonce);
                            
                            $window.location.href = authUrl;
                        });
                    },
                    
                    parseHash: function(callback) {
                        $log.debug("==== OneLogin parseHash called ====");
                        $log.debug("Current URL:", $window.location.href);
                        $log.debug("URL hash:", $window.location.hash);
                        $log.debug("URL search params:", $window.location.search);

                        // Before calling signinRedirectCallback(), verify the URL actually
                        // contains auth callback parameters. 
                        const urlHash = $window.location.hash || "";
                        const urlSearch = $window.location.search || "";
                        const hasAuthCallbackParams =
                            urlHash.includes("access_token") || urlHash.includes("error") ||
                            urlSearch.includes("access_token") || urlSearch.includes("error") ||
                            urlSearch.includes("code=");

                        if (!hasAuthCallbackParams) {
                            $log.debug("parseHash: URL does not contain auth callback parameters, skipping callback processing.");
                            callback(null, null);
                            return;
                        }

                        $log.debug("parseHash: Auth callback parameters detected, processing...");
                        $log.debug("userManager available:", !!userManager);

                        if (userManager) {
                            $log.debug("Attempting oidc-client for callback processing first...");
                            // Try oidc-client first, but be more aggressive about falling back
                            userManager.signinRedirectCallback().then(user => {
                                if (user) {
                                    $log.debug("✅ Authentication successful via oidc-client. Full user object:", user);
                                    $log.debug("parseHash (oidc-client): localStorage before storing id_token:", JSON.stringify(Object.fromEntries(Object.entries(localStorage))));
                                    const authResult = {
                                        accessToken: user.access_token,
                                        idToken: user.id_token,
                                        expiresIn: user.expires_in,
                                        tokenType: user.token_type,
                                        scope: user.scope,
                                        profile: user.profile
                                    };
                                    // Store id_token in localStorage for logout (same as manual path)
                                    if (user.id_token) {
                                        $log.debug("Storing id_token in localStorage for logout (oidc-client path):", user.id_token);
                                        localStorage.setItem("id_token", user.id_token);
                                    } else {
                                        $log.warn("No id_token found in user object (oidc-client path), not storing to localStorage");
                                    }
                                    // Enhance with custom claims expected by TrackerBase API
                                    const enhancedResult = enhanceAuthResultWithCustomClaims(authResult, user);
                                    callback(null, enhancedResult);
                                } else {
                                    $log.debug("ℹ️ No user returned from oidc-client, trying manual parsing");
                                    this.parseHashManually(callback);
                                }
                            }).catch(error => {
                                $log.warn("⚠️ oidc-client callback failed (this is common with implicit flow):", error.message);
                                $log.debug("🔄 Falling back to manual hash parsing (more reliable for implicit flow)...");
                                this.parseHashManually(callback);
                            });
                        } else {
                            $log.debug("No userManager - using manual hash parsing");
                            this.parseHashManually(callback);
                        }
                    },
                    
                    parseHashManually: function(callback) {
                        $log.debug("🔧 Starting manual hash parsing for implicit flow");
                        $log.debug("Current URL (full):", $window.location.href);
                        
                        // Parse the URL hash for tokens (implicit flow)
                        const hash = $window.location.hash.slice(1);
                        $log.debug("Raw hash to parse:", hash);
                        
                        if (!hash) {
                            $log.warn("No hash fragment found in URL - may not be a callback");
                            $log.debug("Checking search params as fallback...");
                            const search = $window.location.search.slice(1);
                            $log.debug("Search params:", search);
                            
                            if (!search || !search.includes("access_token")) {
                                callback(null, null);
                                return;
                            }
                            // Try parsing search params as fallback
                            const searchParams = new URLSearchParams(search);
                            if (searchParams.has("access_token")) {
                                $log.debug("Found tokens in search params instead of hash");
                                this.processTokenParams(searchParams, callback);
                                return;
                            }
                            callback(null, null);
                            return;
                        }
                        
                        // Try URLSearchParams first
                        let params = new URLSearchParams(hash);
                        $log.debug("URLSearchParams result:", Array.from(params.entries()));
                        
                        // If URLSearchParams doesn"t find access_token, try manual parsing
                        if (!params.has("access_token") && hash.includes("access_token")) {
                            $log.debug("URLSearchParams failed, trying manual parsing...");
                            params = this.parseHashManuallyFallback(hash);
                        }
                        
                        this.processTokenParams(params, callback);
                    },
                    
                    parseHashManuallyFallback: function(hash) {
                        // Manual parsing for complex hash fragments that URLSearchParams might not handle
                        const params = new Map();
                        
                        // Split by & and then by =
                        const pairs = hash.split("&");
                        for (const pair of pairs) {
                            const [key, ...valueParts] = pair.split("=");
                            if (key && valueParts.length > 0) {
                                const value = decodeURIComponent(valueParts.join("="));
                                params.set(key, value);
                            }
                        }
                        
                        // Convert Map to URLSearchParams-like interface
                        const result = {
                            has: (key) => params.has(key),
                            get: (key) => params.get(key),
                            entries: () => params.entries()
                        };
                        
                        return result;
                    },
                    
                    processTokenParams: function(params, callback) {
                        $log.debug("==== processTokenParams called ====");
                        $log.debug("processTokenParams: localStorage at entry:", JSON.stringify(Object.fromEntries(Object.entries(localStorage))));
                        let parsedParams;
                        
                        // Handle both URLSearchParams and our custom Map-like interface
                        if (params.entries && typeof params.entries === "function") {
                            parsedParams = Array.from(params.entries());
                        } else {
                            parsedParams = "URLSearchParams fallback";
                        }
                        
                        $log.debug("Processing token parameters:", parsedParams);
                        
                        // Check for authentication errors first
                        if (params.has("error")) {
                            const errorCode = params.get("error");
                            const errorDesc = params.get("error_description") || params.get("error");
                            const error = new Error(`OneLogin authentication error: ${errorCode} - ${errorDesc}`);
                            $log.error("❌ OneLogin auth error:", error);
                            callback(error);
                            return;
                        }
                        
                        // Check for required tokens
                        if (params.has("access_token")) {
                            $log.debug("✅ Access token found in parameters");
                            // Validate state if available (but don"t fail if missing since oidc-client might have cleared it)
                            const returnedState = params.get("state");
                            const storedState = $window.sessionStorage.getItem("oidc_state");
                            $log.debug("State validation:", {
                                returned: returnedState, 
                                stored: storedState,
                                match: returnedState === storedState 
                            });
                            if (storedState && returnedState && returnedState !== storedState) {
                                $log.warn("⚠️ State mismatch detected, but continuing anyway (oidc-client may have interfered)");
                                // Don"t fail on state mismatch in manual mode - log it but continue
                            }
                            const authResult = {
                                accessToken: params.get("access_token"),
                                idToken: params.get("id_token"),
                                expiresIn: parseInt(params.get("expires_in")) || 3600,
                                tokenType: params.get("token_type") || "Bearer",
                                scope: params.get("scope"),
                                state: returnedState
                            };
                            $log.debug("✅ Manual parsing successful:", {
                                hasAccessToken: !!authResult.accessToken,
                                accessTokenLength: authResult.accessToken ? authResult.accessToken.length : 0,
                                hasIdToken: !!authResult.idToken,
                                idTokenLength: authResult.idToken ? authResult.idToken.length : 0,
                                expiresIn: authResult.expiresIn,
                                scope: authResult.scope,
                                tokenType: authResult.tokenType,
                                accessTokenPreview: authResult.accessToken ? authResult.accessToken.substring(0, 20) + "..." : "none"
                            });
                            // Store id_token in localStorage for logout
                            if (authResult.idToken) {
                                $log.debug("Storing id_token in localStorage for logout:", authResult.idToken);
                                localStorage.setItem("id_token", authResult.idToken);
                            } else {
                                $log.warn("No id_token found in authResult, not storing to localStorage");
                            }
                            // Clean up stored state
                            if (storedState) {
                                $window.sessionStorage.removeItem("oidc_state");
                                $window.sessionStorage.removeItem("oidc_nonce");
                                $log.debug("🧹 Cleaned up stored OIDC state");
                            }
                            // Enhance with custom claims expected by TrackerBase API
                            const enhancedResult = enhanceAuthResultWithCustomClaims(authResult, null);
                            callback(null, enhancedResult);
                        } else {
                            $log.warn("⚠️ No access_token found in parameters - may not be a valid callback");
                            // eslint-disable-next-line no-unused-vars
                            $log.debug("Available parameter keys:", parsedParams ? parsedParams.map(([key, value]) => key) : "Could not enumerate");
                            
                            // Debug: try to see if access_token is there but not detected
                            if (typeof params.get === "function") {
                                $log.debug("Debugging parameter access:");
                                $log.debug("  access_token:", params.get("access_token"));
                                $log.debug("  id_token:", params.get("id_token"));
                                $log.debug("  state:", params.get("state"));
                            }
                            
                            callback(null, null);
                        }
                    },
                    
                    checkSession: function(options, callback) {
                        $log.debug("OneLogin checkSession called");
                        
                        if (!userManager) {
                            callback(null, null);
                            return;
                        }
                        
                        userManager.getUser().then(user => {
                            if (user && !user.expired) {
                                callback(null, {
                                    accessToken: user.access_token,
                                    idToken: user.id_token
                                });
                            } else {
                                callback(null, null);
                            }
                        }).catch(error => {
                            $log.error("Check session failed:", error);
                            callback(error);
                        });
                    },
                    
                    clearTokens: function() {
                        $log.debug("Clearing OneLogin tokens from localStorage and sessionStorage");
                        // Clear OneLogin tokens
                        localStorage.removeItem("access_token");
                        localStorage.removeItem("id_token");
                        localStorage.removeItem("expires_at");
                        
                        // Clear AuthService tokens to keep them in sync
                        localStorage.removeItem("tbAccessToken");
                        localStorage.removeItem("tbIdToken");
                        localStorage.removeItem("tbExpiresAt");
                        
                        // Clear session storage completely
                        sessionStorage.clear();
                        
                        $log.debug("All authentication tokens cleared");
                    },
                    
                    logout: function(options) {
                        const domain = config.domain.replace(/^https?:\/\//, "");
                        const idToken = localStorage.getItem("id_token");

                        $log.debug("OneLogin logout - id_token found:", idToken ? "YES" : "NO");

                        const returnUrl = (options && options.returnTo) ? options.returnTo : config.postLogoutRedirectUri;

                        if (idToken) {
                            // OIDC logout with id_token_hint and post_logout_redirect_uri
                            let logoutUrl = `https://${domain}/oidc/2/logout`;
                            const params = new URLSearchParams();

                            params.append("id_token_hint", idToken);
                            if (returnUrl) {
                                params.append("post_logout_redirect_uri", returnUrl);
                            }

                            if (params.toString()) {
                                logoutUrl += "?" + params.toString();
                            }

                            $log.debug("OneLogin logout URL:", logoutUrl);

                            this.clearTokens();
                            $window.location.href = logoutUrl;
                        } else {
                            // No id_token - user was denied at SSO level, still log out from OneLogin
                            $log.warn("OneLogin logout: No id_token - logging out from SSO session only");

                            let logoutUrl = `https://${domain}/oidc/2/logout`;

                            this.clearTokens();
                            $window.location.href = logoutUrl;
                        }
                    },
                    
                    client: {
                        userInfo: function(accessToken, callback) {
                            $log.debug("OneLogin userInfo called with token:", accessToken);

                            if (!userManager) {
                                //Local storage fallback for user info across tabs
                                $log.debug("UserManager not initialized, attempting to decode id_token from localStorage");
                                this.getUserInfoFromIdToken(callback);
                                return;
                            }

                            // Get user info via oidc-client
                            userManager.getUser().then(user => {
                                if (user && user.profile) {
                                    $log.debug("User info from oidc-client:", user.profile);
                                    callback(null, user.profile);
                                } else {
                                    //Local storage fallback for user info across tabs
                                    $log.debug("No user from oidc-client UserManager, attempting to decode id_token from localStorage");
                                    this.getUserInfoFromIdToken(callback);
                                }
                            }).catch(error => {
                                $log.warn("User info error from oidc-client, falling back to id_token:", error);
                                this.getUserInfoFromIdToken(callback);
                            });
                        },

                        getUserInfoFromIdToken: function(callback) {
                            // Local storage fallback for user info across tabs
                            // The userManager won"t have the user object because the oidc-client uses sessionStorage which isn"t shared across tabs). 
                            const idToken = localStorage.getItem("id_token");

                            if (!idToken) {
                                $log.error("No id_token found in localStorage");
                                callback(new Error("No user profile available and no id_token in storage"));
                                return;
                            }

                            try {
                                // Decode the JWT payload (second part of the token)
                                const base64Payload = idToken.split(".")[1];
                                const decodedPayload = atob(base64Payload);
                                const profile = JSON.parse(decodedPayload);

                                $log.debug("Successfully decoded user profile from id_token:", profile);
                                callback(null, profile);
                            } catch (error) {
                                $log.error("Failed to decode id_token:", error);
                                callback(new Error("Failed to decode id_token: " + error.message));
                            }
                        }
                    }
                };
            }];
        });
}

export default oneLoginModuleName;



//////////////////
// WEBPACK FOOTER
// ./src/shims/oneLoginOidc.js
// module id = KBaB
// module chunks = 1